<template>
  <div v-show="isCustomFieldValid" class="tw-flex tw-items-center">
    <div class="tw-flex tw-items-center">
      <toggle
        v-if="isToggleField"
        v-model="localVal"
        :primary-color="Colors.GREEN"
        :disabled="props.readonly"
        :label="props.hideLabel ? '' : customField.name"
        @update:model-value="emitValue"
      />
      <q-checkbox
        v-else-if="isCheckboxField"
        v-model="localVal"
        dense
        size="xs"
        :label="props.hideLabel ? '' : customField.name"
        class="custom-checkbox"
        :disable="props.readonly"
        @update:model-value="emitValue"
      />
    </div>
    <help-text
      v-if="!!customField.description && !hideLabel"
      class="tw-mb-0.5 tw-ml-1"
    >
      {{ customField.description }}
    </help-text>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import Toggle from '../core/CoreToggle.vue';
import HelpText from '../design-system/misc/HelpText.vue';
import {
  Colors,
  CustomField,
  CustomFieldType,
  CustomFieldBooleanSubType,
} from '@/api';

interface Props {
  customField: CustomField;
  value?: boolean;
  hideLabel?: boolean;
  readonly?: boolean;
}
interface Emits {
  (e: 'value-updated', value: boolean): void;
}

const props = withDefaults(defineProps<Props>(), {});
const emit = defineEmits<Emits>();

const isCustomFieldValid = computed(() => {
  return props.customField.type === CustomFieldType.BOOLEAN;
});

const isToggleField = computed(() => {
  return props.customField.subType === CustomFieldBooleanSubType.SWITCH;
});

const isCheckboxField = computed(() => {
  return props.customField.subType === CustomFieldBooleanSubType.CHECKBOX;
});

const localVal = ref<boolean>(props.value || false);
watch(
  () => props.value,
  (newValue) => {
    localVal.value = newValue || false;
  }
);

async function emitValue() {
  await nextTick();
  emit('value-updated', localVal.value);
}

const isValid = computed(() => {
  return true; // nothing to validate for now, this is defined for consistency with other custom fields
});

function validate() {
  // nothing to validate for now, this is defined for consistency with other custom fields
}

function removeError() {
  // nothing to remove for now, this is defined for consistency with other custom fields
}

defineExpose({
  validate,
  removeError,
  isValid,
});
</script>
<style lang="scss" scoped>
.custom-checkbox {
  @apply tw-bg-white tw-w-full;

  :deep(.q-checkbox__inner) {
    @apply tw-border tw-border-transparent tw-rounded;
  }

  &:hover:not(.disabled) :deep(.q-checkbox__inner) {
    @apply tw-border-gray-dark;
  }

  &:focus :deep(.q-checkbox__inner) {
    @apply tw-border-primary;
  }

  :deep(.q-checkbox__label) {
    @apply tw-text-xs;
  }
}
</style>
